import React, {useState, useEffect, useCallback} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import '@sweetalert2/theme-dark/dark.min.css';
import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faAngleDown,
    faAngleUp,

} from "@fortawesome/free-solid-svg-icons";

function SuperAdminUserManagement() {
    const [users, setUsers] = useState([]);
    const [farms, setFarms] = useState([]);
    const [ngoFarmIds, setNgoFarmIds] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        role: 'farmer',
        farm_id: '',
        ngo_farm_ids: [],
        father_or_husband_name:'',
        gender: '',
        date_of_birth: '',
        nid_or_birth_cert_no: '',
        education_level: '',
        address: '',
    });
    const [editing, setEditing] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        total: 0,
        currentPage: 1,
        totalPages: 1,
    });
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [pageSize] = useState(6); // Number of items per page
    const { t } = useTranslation();
    const fetchUsers = useCallback(async (page = 1,searchTerm) => {
        const token = localStorage.getItem('token');
        let apiUrl = `${process.env.REACT_APP_BASE_URL}/api/auth/all/users/farmers?page=${page}&limit=${pageSize}`;
        if(searchTerm){
            apiUrl = `${process.env.REACT_APP_BASE_URL}/api/auth/all/users/farmers?page=1&limit=${pageSize}&search=${searchTerm}`;
        }
        try {
            const response = await axios.get(apiUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setUsers(response.data.data);
            setPagination({
                total: response.data.total,
                currentPage: response.data.currentPage,
                totalPages: response.data.totalPages,
            });
            setCurrentPage(response.data.currentPage);
            setLoading(false);
        } catch (error) {
            Swal.fire('Error', 'Failed to fetch users.', 'error');
        }
    }, [pageSize]);

    const fetchFarms = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/farm/all/for-admin`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setFarms(response.data.farms); // Assuming the API response is the list of farms
        } catch (error) {
            Swal.fire('Error', 'Failed to fetch farms.', 'error');
        }
    };

    useEffect(() => {
        fetchUsers(currentPage,searchTerm);
        fetchFarms(); // Fetch farms when component mounts
    }, [currentPage, searchTerm,fetchUsers]);



    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const toggleExpand = (id) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        try {
            if(ngoFarmIds.length > 0){
                formData.ngo_farm_ids = ngoFarmIds.map(item=>item.value);
            }
            if (editing) {
                await axios.put(`${process.env.REACT_APP_BASE_URL}/api/auth/farmer/user/update/${editing._id}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                Swal.fire('Success', 'User updated successfully', 'success');
            } else {
                await axios.post(`${process.env.REACT_APP_BASE_URL}/api/auth/farmer/user/store`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                Swal.fire('Success', 'User created successfully', 'success');
            }
            setFormData({  name: '', email: '', password: '', phone: '',
                role: 'farmer', farm_id: '', ngo_farm_ids: [],
                father_or_husband_name: '', gender: '', date_of_birth: '',
                nid_or_birth_cert_no: '', education_level: '', address: '' });
            setNgoFarmIds([]);
            setEditing(null);
            setIsModalOpen(false);
            fetchUsers(currentPage,searchTerm);
        } catch (error) {
            Swal.fire('Error', 'Failed to save user.', 'error');
        }
    };

    const handleEdit = (user) => {
        console.log(user);
        if(user.adminFarms && user.adminFarms.farm_ids){
            const farmsIdsToAll = farms.map(f=>{
                return {
                    label: f.name,
                    value: f._id,
                };
            });
            const userFarmIds = user.adminFarms?.farm_ids ?? [];
            const farmsIdsToSet = farmsIdsToAll.filter(f=>userFarmIds.includes(f.value));
            setNgoFarmIds(farmsIdsToSet);
        }
        setFormData({
            name: user.name,
            email: user.email,
            phone: user.phone,
            password: '', // Clear password for editing
            role: user.roles[0].name,
            farm_id: user.farms?._id ?? '',
            father_or_husband_name: user.father_or_husband_name ?? '',
            gender: user.gender ?? '',
            date_of_birth: user.date_of_birth ? user.date_of_birth.substring(0, 10) : '',
            nid_or_birth_cert_no: user.nid_or_birth_cert_no ?? '',
            education_level: user.education_level ?? '',
            address: user.address ?? '',
        });

        setEditing(user);
        setIsModalOpen(true);
    };

    // const handleDelete = async (id) => {
    //     const token = localStorage.getItem('token');
    //     try {
    //         const result = await Swal.fire({
    //             title: 'Are you sure?',
    //             text: 'Do you want to delete this user?',
    //             icon: 'warning',
    //             showCancelButton: true,
    //             confirmButtonColor: '#d33',
    //             cancelButtonColor: '#3085d6',
    //             confirmButtonText: 'Yes, delete it!',
    //         });
    //
    //         if (result.isConfirmed) {
    //             await axios.delete(`${process.env.REACT_APP_BASE_URL}/api/auth/all/users/farmers/${id}`, {
    //                 headers: {
    //                     Authorization: `Bearer ${token}`,
    //                 },
    //             });
    //             Swal.fire('Deleted!', 'User has been deleted.', 'success');
    //             fetchUsers(currentPage,searchTerm);
    //         }
    //     } catch (error) {
    //         Swal.fire('Error', 'Failed to delete user.', 'error');
    //     }
    // };

    const openModal = () => {
        setNgoFarmIds([]);
        setFormData({ name: '',
            email: '',
            phone: '',
            password: '',
            role: 'farmer',
            farm_id: '',
            ngo_farm_ids: [],
            father_or_husband_name:'',
            gender: '',
            date_of_birth: '',
            nid_or_birth_cert_no: '',
            education_level: '',
            address: '' });
        setEditing(null);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const handlePageChange = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= pagination.totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const getPaginationButtons = () => {
        const { currentPage, totalPages } = pagination;
        const pageNumbers = [];

        let startPage = Math.max(currentPage - 2, 1);
        let endPage = Math.min(currentPage + 2, totalPages);

        if (startPage > 1) {
            pageNumbers.push(1);
            if (startPage > 2) pageNumbers.push('...');
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        if (endPage < totalPages - 1) pageNumbers.push('...');
        if (endPage < totalPages) pageNumbers.push(totalPages);

        return pageNumbers;
    };

    return (
        <div className="container mx-auto p-6">
            <h1 className="text-3xl font-bold mb-6">{t('user_management')}</h1>
            <button
                onClick={openModal}
                className="bg-blue-500 text-white p-3 rounded mb-6 hover:bg-blue-600 transition duration-200"
            >
                {t('add_user')}
            </button>

            <div className="bg-white shadow-md rounded-lg p-6">
                <h2 className="text-2xl font-semibold mb-4">{t('user_list')}</h2>
                <div className="flex items-center justify-between mb-4">
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder={t('search_user')}
                        className="w-1/2 px-4 py-2 border rounded"
                    />
                </div>
                {loading ? (
                    <div>Loading...</div>
                ) :users.length > 0 ? (
                    <table className="min-w-full bg-white">
                        <thead>
                        <tr>
                            <th className="py-2 px-4 border-b"></th>
                            <th className="py-2 px-4 border-b">{t('name')}</th>
                            <th className="py-2 px-4 border-b">{t('email')}</th>
                            <th className="py-2 px-4 border-b">{t('phone')}</th>
                            <th className="py-2 px-4 border-b">{t('role')}</th>
                            <th className="py-2 px-4 border-b">{t('farm')}</th>
                            <th className="py-2 px-4 border-b">{t('actions')}</th>
                        </tr>
                        </thead>

                        <tbody>
                        {users.map((user) => {
                            const isOpen = expandedRows.has(user._id);
                            const prettyGender = user.gender
                                ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1)
                                : '';
                            const dob = user.date_of_birth
                                ? new Date(user.date_of_birth).toISOString().slice(0, 10) // YYYY-MM-DD
                                : '';
                            const edu = user.education_level
                                ? user.education_level.replace('_', ' ')
                                : '';

                            return (
                                <React.Fragment key={user._id}>
                                    {/* Main row: 6 columns only */}
                                    <tr>
                                        <td className="py-2 px-4 border-b text-center"><button
                                            onClick={() => toggleExpand(user._id)}
                                            className="bg-gray-200 text-gray-800 px-3 py-1.5 rounded hover:bg-gray-300"
                                            aria-expanded={isOpen}
                                            aria-label={isOpen ? t('hide_details') ?? 'Hide details' : t('show_details') ?? 'Show details'}
                                            title={isOpen ? (t('hide_details') ?? 'Hide details') : (t('show_details') ?? 'Show details')}
                                        >
                                            {isOpen ? <FontAwesomeIcon icon={faAngleUp } /> : <FontAwesomeIcon icon={faAngleDown} />}
                                        </button></td>
                                        <td className="py-2 px-4 border-b text-center">{user.name}</td>
                                        <td className="py-2 px-4 border-b text-center">{user.email}</td>
                                        <td className="py-2 px-4 border-b text-center">{user.phone}</td>
                                        <td className="py-2 px-4 border-b text-center">
                                            {user?.roles?.[0]?.name ?? ''}
                                        </td>
                                        <td className="py-2 px-4 border-b text-center">
                                            {user?.farms?.name ?? ''}
                                        </td>
                                        <td className="py-2 px-4 border-b text-center space-x-2">
                                            <button
                                                onClick={() => handleEdit(user)}
                                                className="bg-yellow-500 text-white px-3 py-1.5 rounded hover:bg-yellow-600"
                                            >
                                                {t('edit')}
                                            </button>

                                            {/* Plus / minus toggle */}

                                        </td>
                                    </tr>

                                    {/* Details row: spans all 6 columns */}
                                    {isOpen && (
                                        <tr className="bg-gray-50">
                                            <td colSpan={6} className="py-3 px-4 border-b">
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                    <div>
                                                        <div className="text-xs text-gray-500">{t('father_or_husband_name') || 'Father/Husband Name'}</div>
                                                        <div className="font-medium">{user.father_or_husband_name ?? ''}</div>
                                                    </div>

                                                    <div>
                                                        <div className="text-xs text-gray-500">{t('gender') || 'Gender'}</div>
                                                        <div className="font-medium">{prettyGender}</div>
                                                    </div>

                                                    <div>
                                                        <div className="text-xs text-gray-500">{t('date_of_birth') || 'Date of Birth'}</div>
                                                        <div className="font-medium">{dob}</div>
                                                    </div>

                                                    <div>
                                                        <div className="text-xs text-gray-500">{t('nid_or_birth_cert_no') || 'NID / Birth Cert No.'}</div>
                                                        <div className="font-medium">{user.nid_or_birth_cert_no ?? ''}</div>
                                                    </div>

                                                    <div>
                                                        <div className="text-xs text-gray-500">{t('education_level') || 'Education Level'}</div>
                                                        <div className="font-medium capitalize">{edu}</div>
                                                    </div>

                                                    <div className="md:col-span-3">
                                                        <div className="text-xs text-gray-500">{t('address') || 'Address'}</div>
                                                        <div className="font-medium break-words">{user.address ?? ''}</div>
                                                    </div>

                                                    {/* Optional: show Admin Farms list if you want */}
                                                    {user?.adminFarms?.farm_ids?.length ? (
                                                        <div className="md:col-span-3">
                                                            <div className="text-xs text-gray-500">{t('admin_farm_ids') || 'Admin Farm IDs'}</div>
                                                            <div className="font-medium">
                                                                {user.adminFarms.farm_ids.join(', ')}
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        </tbody>
                    </table>


                ) : (
                    <p>No users found.</p>
                )}
                {pagination.total > 0 && (
                    <div className="mt-6 flex justify-center items-center space-x-2">
                        <button
                            onClick={() => handlePageChange(1)}
                            className={`px-3 py-1 rounded ${pagination.currentPage === 1 ? 'bg-gray-300' : 'bg-gray-200 text-gray-800'}`}
                            disabled={pagination.currentPage === 1}
                        >
                            {t('first')}
                        </button>
                        <button
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            className={`px-3 py-1 rounded ${pagination.currentPage === 1 ? 'bg-gray-300' : 'bg-gray-200 text-gray-800'}`}
                            disabled={pagination.currentPage === 1}
                        >
                            {t('previous')}
                        </button>

                        {getPaginationButtons().map((page, index) =>
                            page === '...' ? (
                                <span key={index} className="px-3 py-1">...</span>
                            ) : (
                                <button
                                    key={index}
                                    onClick={() => handlePageChange(page)}
                                    className={`px-3 py-1 rounded ${
                                        pagination.currentPage === page ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800'
                                    }`}
                                >
                                    {page}
                                </button>
                            )
                        )}

                        <button
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            className={`px-3 py-1 rounded ${pagination.currentPage === pagination.totalPages ? 'bg-gray-300' : 'bg-gray-200 text-gray-800'}`}
                            disabled={pagination.currentPage === pagination.totalPages}
                        >
                            {t('next')}
                        </button>
                        <button
                            onClick={() => handlePageChange(pagination.totalPages)}
                            className={`px-3 py-1 rounded ${pagination.currentPage === pagination.totalPages ? 'bg-gray-300' : 'bg-gray-200 text-gray-800'}`}
                            disabled={pagination.currentPage === pagination.totalPages}
                        >
                            {t('last')}
                        </button>
                    </div>
                )}
            </div>

            {isModalOpen && (
                <div className="fixed z-50 inset-0 overflow-y-auto">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                                    {editing ? t('update_user') : t('add_user')}
                                </h3>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-gray-700">{t('name')}</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700">{t('phone')}</label>
                                        <input
                                            type="text"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700">{t('email')}</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-gray-700">{t('password')}</label>
                                        <input
                                            type="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                            required={!editing} // Required only when adding a new user
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700">{t('farm')}</label>
                                        <select
                                            name="farm_id"
                                            value={formData.farm_id ?? ''}
                                            onChange={handleChange}
                                            required={formData.role === "farmer"}
                                            className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                        >
                                            <option value="">{t('select_farm')}</option>
                                            {farms.map((farm) => (
                                                <option key={farm._id} value={farm._id}>
                                                    {farm.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                      <label className="block text-gray-700">{t('Father/Husband Name')}</label>
                                      <input
                                        type="text"
                                        name="father_or_husband_name"
                                        value={formData.father_or_husband_name}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-gray-700">{t('Gender')}</label>
                                      <select
                                        name="gender"
                                        value={formData.gender}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                      >
                                        <option value="">{t('Select Gender')}</option>
                                        <option value="male">{t('Male')}</option>
                                        <option value="female">{t('Female')}</option>
                                      </select>
                                    </div>

                                    <div>
                                      <label className="block text-gray-700">{t('Date of Birth')}</label>
                                      <input
                                        type="date"
                                        name="date_of_birth"
                                        value={formData.date_of_birth}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-gray-700">{t('NID / Birth Cert No.')}</label>
                                      <input
                                        type="text"
                                        name="nid_or_birth_cert_no"
                                        value={formData.nid_or_birth_cert_no}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-gray-700">{t('Education Level')}</label>
                                      <select
                                        name="education_level"
                                        value={formData.education_level}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                      >
                                          <option value="">{t('Select Level')}</option>
                                          <option value="no_schooling">{t('No Schooling')}</option>
                                          <option value="primary">{t('Primary')}</option>
                                          <option value="secondary">{t('Secondary')}</option>
                                          <option value="higher_secondary">{t('Higher Secondary')}</option>
                                          <option value="graduate">{t('Graduate')}</option>
                                          <option value="postgraduate">{t('Postgraduate')}</option>
                                      </select>
                                    </div>

                                    <div>
                                      <label className="block text-gray-700">{t('Address')}</label>
                                      <textarea
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                        rows={3}
                                      />
                                    </div>

                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={closeModal}
                                            className="bg-gray-500 text-white p-3 rounded mr-4 hover:bg-gray-600 transition duration-200"
                                        >
                                            {t('cancel')}
                                        </button>
                                        <button
                                            type="submit"
                                            className="bg-blue-500 text-white p-3 rounded hover:bg-blue-600 transition duration-200"
                                        >
                                            {editing ? t('update'): t('add')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SuperAdminUserManagement;
